import log from 'loglevel'
import { getSsh } from './util/getSsh'
import { DeployConfig, deployAction } from '../configuration/interface'
import { validateEnv } from './environments/validateEnvironment'
import { getCurrentEnvironmentConfig } from './environments/deployEnvironment'

export const deploy = async (deployConfig: DeployConfig, action: deployAction) => {
    // Validate environments
    validateEnv(deployConfig.requiredEnvVars)
    const environmentConfig = getCurrentEnvironmentConfig(deployConfig)
    const { environmentName } = environmentConfig

    if (action === 'revert' && !environmentConfig.revertible) {
        throw new Error(`Environment "${environmentName}" is not revertible. Set "revertible: true" in its config to keep the previous build on the server.`)
    }

    log.info(`Running deploy for environment: ${environmentName} with action ${action}`)

    const servers = environmentConfig.servers

    // Process all configured servers
    // Using for loop so all awaited promises must be resolved before next cycle
    for (let index = 0; index < servers.length; index++) {
        const serverConfig = servers[index]

        log.info(`Processing host "${serverConfig.sshConfig.host}"`)

        // Add user created dynamicArgs (can be mutated in any deploy function, usually deploy before)
        const dynamicArgs: { [key: string]: any } = {}

        const ssh = await getSsh(serverConfig.sshConfig)

        try {
            const nill = async () => { }

            // Setup dynamicArgs
            await (deployConfig.setupDynamicArgs || environmentConfig.setupDynamicArgs || nill)(deployConfig, environmentConfig, serverConfig, dynamicArgs, ssh);
            // Run before deploy hook
            if (action === 'default') {
                await (environmentConfig.deployBefore || deployConfig.deployBefore || nill)(deployConfig, environmentConfig, serverConfig, dynamicArgs, ssh);
                // Upload files from runner to server
                await (environmentConfig.deployUpload || deployConfig.deployUpload)(deployConfig, environmentConfig, serverConfig, dynamicArgs, ssh);
                // Start uploaded build
                await (environmentConfig.deployStart || deployConfig.deployStart)(deployConfig, environmentConfig, serverConfig, dynamicArgs, ssh);
                // Run after deploy hook
                await (environmentConfig.deployAfter || deployConfig.deployAfter || nill)(deployConfig, environmentConfig, serverConfig, dynamicArgs, ssh);
            }

            if (action === 'stop') {
                await (environmentConfig.onStop || deployConfig.onStop)(deployConfig, environmentConfig, serverConfig, dynamicArgs, ssh);
            }

            if (action === 'revert') {
                // Switch the app back to the previous build kept on the server
                await (environmentConfig.onRevert || deployConfig.onRevert)(deployConfig, environmentConfig, serverConfig, dynamicArgs, ssh);
            }
        } finally {
            // Dispose this server's ssh connection even when a hook throws
            ssh.dispose()
        }
    }
}
